import BigNumber from 'bignumber.js';
import _ from 'lodash';
import mixins from 'vue-typed-mixins';

import { E集計転記科目 } from '@/openapi/enum';

import C勘定科目三表計画Import from '@/components/勘定科目/import/勘定科目三表計画Import.vue';
import C科目別TableRow from '@/components/科目別TableRow/科目別TableRow.vue';

import PlbscfKeystrokeInputableTextMixin, {
  PlbscfInputableTextId,
} from '@/entries/user/mixins/PlbscfKeystrokeInputableTextMixin';
import Plbscf要約表示Mixin from '@/entries/user/mixins/Plbscf要約表示Mixin';
import RoutesMixin from '@/entries/user/mixins/RoutesMixin';
import 計画三表Mixin from '@/entries/user/mixins/計画三表Mixin';
import 計画三表TableMixin from '@/entries/user/mixins/計画三表TableMixin';
import 計画貸借対照表Mixin from '@/entries/user/mixins/計画貸借対照表Mixin';

import { MONTHS_OF_YEAR } from '@/classes/common/Constants';
import { PlbscfListItem, PlbscfYearValue, 集計転記科目Key } from '@/classes/service/AbstractPlbscfService';
import { Bs } from '@/classes/service/Abstract計画PlbscfService';
import { PlbscfType } from '@/master/Plbscf';
import 決算期Utility from '@/classes/common/決算期Utility';

enum ECalcOptionType {
  前期の回転期間,
  回転期間指定,
  棚卸高転記,
}

type CalcDialogOption = {
  id: number;
  type: ECalcOptionType;
  // type = 棚卸高転記 の場合設定
  転記対象?:
    | E集計転記科目.期末商品棚卸高
    | E集計転記科目.期末製品棚卸高
    | E集計転記科目.期末材料棚卸高
    | E集計転記科目.期末仕掛品棚卸高;
};

const dialogOptions: CalcDialogOption[] = [
  {
    id: 1,
    type: ECalcOptionType.前期の回転期間,
  },
  {
    id: 2,
    type: ECalcOptionType.回転期間指定,
  },
  {
    id: 3,
    type: ECalcOptionType.棚卸高転記,
    転記対象: E集計転記科目.期末商品棚卸高,
  },
  {
    id: 4,
    type: ECalcOptionType.棚卸高転記,
    転記対象: E集計転記科目.期末製品棚卸高,
  },
  {
    id: 5,
    type: ECalcOptionType.棚卸高転記,
    転記対象: E集計転記科目.期末材料棚卸高,
  },
  {
    id: 6,
    type: ECalcOptionType.棚卸高転記,
    転記対象: E集計転記科目.期末仕掛品棚卸高,
  },
];

const 棚卸転記科目s = [
  E集計転記科目.棚卸資産PL期末商品,
  E集計転記科目.棚卸資産PL期末製品,
  E集計転記科目.棚卸資産CR期末材料,
  E集計転記科目.棚卸資産CR期末仕掛品,
];

export default mixins(
  PlbscfKeystrokeInputableTextMixin,
  Plbscf要約表示Mixin,
  RoutesMixin,
  計画三表Mixin,
  計画三表TableMixin,
  計画貸借対照表Mixin
).extend({
  name: 'C貸借対照表',

  components: {
    C科目別TableRow,
    C勘定科目三表計画Import,
  },

  data(): {
    calcDialog: {
      listItem: PlbscfListItem | null;
      option: CalcDialogOption;
      回転期間: number;
    };
  } {
    return {
      calcDialog: {
        listItem: null,
        option: dialogOptions[0],
        回転期間: 0,
      },
    };
  },

  computed: {
    bs(): Bs {
      return this.計画Plbscf.bs;
    },
    bsListItems(): PlbscfListItem[] {
      return this.filteredPlbscfListItems(this.bs.list);
    },
    平均月商YearValues(): PlbscfYearValue[] {
      const 運転資金TypeList = this.計画Plbscf.types.find((typeList) => typeList.type === PlbscfType.運転資金);
      const listItem = 運転資金TypeList?.list.find((listItem) => listItem.config.label.name === '平均月商');
      return listItem === undefined ? [] : listItem.yearValues;
    },
    前期Year(): number | undefined {
      return this.bs.貸借CalcConfig?.前期Year;
    },
    map(): PlbscfInputableTextId[][] {
      return this.is補助科目別貸借対照表 ? this.補助科目別Map : this.貸借Map;
    },
    貸借Map(): PlbscfInputableTextId[][] {
      return this.createMap(this.bsListItems);
    },
    補助科目別Map(): PlbscfInputableTextId[][] {
      const map: PlbscfInputableTextId[][] = [];
      this.計画Plbscf.補助科目別貸借対照表.list.forEach((listItem, listItemIndex) => {
        if (!listItem.canEdit) return;
        const ids: PlbscfInputableTextId[] = [];
        this.addYearIds(ids, listItemIndex, listItem.yearItems);
        if (ids.length > 0) map.push(ids);
      });
      return map;
    },
    is回転期間指定(): boolean {
      return this.calcDialog.option.type === ECalcOptionType.回転期間指定;
    },
    selected集計転記科目(): E集計転記科目 | undefined {
      return this.calcDialog.listItem?.勘定科目?.c集計転記科目;
    },
    集計転記科目Key(): 集計転記科目Key | undefined {
      if (this.selected集計転記科目 === undefined) return undefined;
      return [E集計転記科目.売上債権, E集計転記科目.仕入債務].includes(this.selected集計転記科目)
        ? (this.selected集計転記科目 as 集計転記科目Key)
        : E集計転記科目.棚卸資産;
    },
    calcDialogOptions(): CalcDialogOption[] {
      if (this.selected集計転記科目 === undefined) return [];
      return 棚卸転記科目s.includes(this.selected集計転記科目)
        ? dialogOptions
        : dialogOptions.filter((o) => o.type !== ECalcOptionType.棚卸高転記);
    },
  },

  watch: {
    is補助科目別貸借対照表(): void {
      this.setFirstId();
    },
    keystrokeResetCount: {
      handler(): void {
        this.setFirstId();
      },
      immediate: true,
    },
  },

  destroyed(): void {
    this.inactivate補助科目別貸借対照表();
  },

  methods: {
    is計算ButtonEnabled(listItem: PlbscfListItem): boolean {
      return (
        listItem.勘定科目?.c集計転記科目 !== undefined &&
        [E集計転記科目.売上債権, E集計転記科目.棚卸資産, E集計転記科目.仕入債務, ...棚卸転記科目s].includes(
          listItem.勘定科目.c集計転記科目
        )
      );
    },
    openCalcDialog(listItem: PlbscfListItem): void {
      // 計算ボタンを押下した行データを保持
      this.calcDialog.listItem = listItem;
    },
    closeCalcDialog(): void {
      // 初期化
      this.calcDialog = {
        listItem: null,
        option: dialogOptions[0],
        回転期間: 0,
      };
    },
    clickCalculateButton(): void {
      if (this.selected集計転記科目 === undefined || this.bs.貸借CalcConfig === undefined) {
        this.closeCalcDialog();
        return;
      }
      const listItem = this.calcDialog.listItem!;
      const option = this.calcDialog.option;
      const 前期Year = this.前期Year!;
      const 貸借CalcConfig = this.bs.貸借CalcConfig;

      if (option.type === ECalcOptionType.棚卸高転記) {
        // 棚卸高
        // 転記 (損益(合計)→貸借)
        const plListItem = this.計画Plbscf.pls
          .find((pl) => pl.部門pk === undefined)
          ?.list.find(
            (listItem) =>
              listItem.勘定科目?.c集計転記科目 !== undefined && listItem.勘定科目.c集計転記科目 === option.転記対象
          );
        if (plListItem !== undefined) {
          listItem.yearValues.forEach((yv) => {
            // 過去決算期は処理なし
            if (yv.year <= 前期Year) return;
            const foundYv = plListItem.yearValues.find((y) => y.year === yv.year);
            if (foundYv === undefined) return;
            const mvs = yv.item.monthlyValues;
            if (mvs === undefined) {
              yv.item.value = foundYv.item.value;
            } else {
              // 月のデータが存在する場合
              mvs.forEach((_, i) => {
                mvs[i] = foundYv.item.monthlyValues![i];
                // 期首月（先頭の月）の翌月以降は、前月の値を足す
                if (i > 0) mvs[i] += mvs[i - 1];
              });
              yv.item.value = _.sum(yv.item.monthlyValues);
              yv.optionValue = mvs[MONTHS_OF_YEAR - 1];
            }
          });
        }
      } else {
        // 回転期間
        const key = this.集計転記科目Key!;
        const 回転期間 =
          option.type === ECalcOptionType.前期の回転期間 ? 貸借CalcConfig.回転期間s[key] : this.calcDialog.回転期間;
        // 前期の科目合計値
        const 前期科目合計 = 貸借CalcConfig.集計転記科目合計s[key];
        // 前期の同じ行の値
        const 前期Value = listItem.yearValues.find((yv) => yv.year === 前期Year)?.item.value;

        // 選択行の各yearValueについて計算:= 平均月商 × 回転期間 × (前期の同じ行の値) / (前期の科目合計値)
        listItem.yearValues.forEach((yv) => {
          // 過去決算期は処理なし
          if (yv.year <= 前期Year) return;
          // その年の平均月商
          const 平均月商 = this.平均月商YearValues.find((平均月商yv) => 平均月商yv.year === yv.year)?.calcValue;
          const result =
            平均月商 === undefined || 前期Value === undefined || 前期科目合計 === 0
              ? 0
              : new BigNumber(平均月商)
                  .times(回転期間)
                  .times(前期Value)
                  .div(前期科目合計)
                  .dp(0, BigNumber.ROUND_HALF_UP)
                  .toNumber();
          if (yv.item.monthlyValues === undefined) {
            yv.item.value = result;
          } else {
            // 全ての月に同じ値を設定
            yv.item.monthlyValues.forEach((_, i) => {
              yv.item.monthlyValues![i] = result;
            });
            this.sumMonthlyValues(yv, MONTHS_OF_YEAR - 1, result);
          }
        });

        // 転記 (貸借→損益(本社)　他部門は0)
        switch (this.selected集計転記科目) {
          case E集計転記科目.棚卸資産PL期末商品:
            this.$_transcribeToPls(E集計転記科目.期末商品棚卸高);
            break;
          case E集計転記科目.棚卸資産PL期末製品:
            this.$_transcribeToPls(E集計転記科目.期末製品棚卸高);
            break;
          case E集計転記科目.棚卸資産CR期末材料:
            this.$_transcribeToPls(E集計転記科目.期末材料棚卸高);
            break;
          case E集計転記科目.棚卸資産CR期末仕掛品:
            this.$_transcribeToPls(E集計転記科目.期末仕掛品棚卸高);
            break;
        }
      }

      this.closeCalcDialog();
    },
    adjustDiffValue(): void {
      // 「現金及び預金」「【差異】」の行を取得
      const 現預金ListItem = this.bs.list.find(
        (listItem) => listItem.勘定科目?.c集計転記科目 === E集計転記科目.現金及び預金
      );
      const 差異ListItem = this.bs.list.find(
        (listItem) => listItem.config.label.name === '資産の部・負債及び純資産の部【差異】'
      );
      if (現預金ListItem === undefined || 差異ListItem === undefined) return;
      // 「【差異】」が0になるように、「現金及び預金」の値を調整する
      現預金ListItem.yearValues.forEach((yv, i) => {
        // 過去決算期は処理なし
        if (yv.year <= this.前期Year!) return;
        const 差異yv = 差異ListItem.yearValues[i];
        yv.item.value! -= 差異yv.calcValue!;
        if (差異yv.monthlyCalcValues === undefined) return;
        差異yv.monthlyCalcValues.forEach((mcv, j) => {
          // not checked 計画第1期を月次計画で作成する
          if (yv.year > 決算期Utility.get計画0期の決算年(true) && j == 11) {
            yv.item.monthlyValues![j] -= 差異yv.calcValue!;
          } else {
            yv.item.monthlyValues![j] -= mcv;
          }
        });
      });
    },
    optionLabel(option: CalcDialogOption): string {
      switch (option.type) {
        case ECalcOptionType.前期の回転期間:
          return `平均月商×前期の${this.集計転記科目Key}回転期間`;

        case ECalcOptionType.回転期間指定:
          return `平均月商×${this.集計転記科目Key}回転期間指定`;

        case ECalcOptionType.棚卸高転記:
          if (option.転記対象 === undefined) return '';
          const prefix = [E集計転記科目.期末商品棚卸高, E集計転記科目.期末製品棚卸高].includes(option.転記対象)
            ? 'PL'
            : '製造原価';
          return `${prefix}の${option.転記対象}の数値を転記する`;
      }
    },
    $_transcribeToPls(e集計転記科目: E集計転記科目): void {
      const listItem = this.calcDialog.listItem!;
      const 前期Year = this.前期Year!;

      // 転記 (貸借→損益(本社)　他部門は0)
      this.計画Plbscf.pls
        .filter((pl) => pl.部門pk !== undefined)
        .forEach((pl) => {
          pl;
          const plListItem = pl.list.find((listItem) => listItem.勘定科目?.c集計転記科目 === e集計転記科目);
          if (plListItem === undefined) return;

          plListItem.yearValues.forEach((yv) => {
            // 過去決算期は処理なし
            if (yv.year <= 前期Year) return;
            const mvs = yv.item.monthlyValues;
            if (pl.部門pk === this.service.get部門s[0].pk) {
              // 先頭の部門の場合、転記
              const foundYv = listItem.yearValues.find((y) => y.year === yv.year);
              if (foundYv === undefined) return;
              if (mvs === undefined) {
                yv.item.value = foundYv.item.value;
              } else {
                // 月のデータが存在する場合
                mvs.forEach((_, i) => {
                  mvs[i] = foundYv.item.monthlyValues![i];
                  // 期首月（先頭の月）の翌月以降は、前月の値を引く
                  if (i > 0) mvs[i] -= foundYv.item.monthlyValues![i - 1];
                });
                yv.item.value = _.sum(mvs);
              }
            } else {
              // 上記以外の部門の場合、0
              yv.item.value = 0;
              if (mvs !== undefined) mvs.forEach((_, i) => (mvs[i] = 0));
            }
          });
        });
    },
  },
});
